import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Accordion,
  Row,
  Col,
  Spinner,
  Alert,
  Button,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import useFetchById from 'hooks/useFetchById';

import SubtleBadge from 'components/common/SubtleBadge';

import { picklistApi } from 'api';
import paths from 'routes/paths';
import { generatePicklistPdf } from 'utils/pdfGenerators';
import { getPicklistBadgeColor } from 'constants/picklist';
import { useUserContext } from '../../../../providers/UserProvider';

const PicklistDetailSummary = ({
  picklistId,
  showActionBtn = false,
  listenToUpdates = false,
}) => {
  const navigate = useNavigate();
  const [activeKeys, setActiveKeys] = useState(['0', '1']);

  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const handleSelect = key => {
    setActiveKeys(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handlePicklistAction = async (picklistStatus, picklistCode) => {
    try {
      if (picklistStatus === 'created') {
        await picklistApi.picklistStatusToReceiving(picklistId);
      }
      navigate(
        `${paths.picklists.process(
          tenant,
          warehouse,
          picklistId,
        )}?picklist_code=${picklistCode}`,
      );
    } catch (error) {
      toast.error('Error updating picklist status');
    }
  };

  const handlePrintPicklist = async (picklistId, picklistCode) => {
    try {
      const response = await picklistApi.getProductBatchesOfPicklist(
        picklistId,
      );
      generatePicklistPdf(response.results, picklistCode);
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const {
    data: picklistData,
    loading,
    error,
    refetch,
  } = useFetchById(picklistApi, picklistId);

  useEffect(() => {
    if (listenToUpdates) {
      const handleUpdate = () => {
        refetch();
      };

      window.addEventListener('picklist-updated', handleUpdate);

      return () => window.removeEventListener('picklist-updated', handleUpdate);
    }
  }, [listenToUpdates, refetch]);

  if (loading) {
    return (
      <Card className="d-flex justify-content-center align-items-center p-4">
        <Spinner animation="border" variant="primary" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {showActionBtn && (
        <div className="d-flex justify-content-end gap-2 mb-3">
          {['created', 'receiving'].includes(picklistData?.picklist_status) && (
            <Button
              variant="falcon-default"
              size="sm"
              onClick={() =>
                handlePicklistAction(
                  picklistData?.picklist_status,
                  picklistData?.picklist_code,
                )
              }
            >
              {picklistData?.picklist_status === 'created' &&
                'Receive Picklist'}
              {picklistData?.picklist_status === 'receiving' &&
                'Resume Picklist'}
            </Button>
          )}

          <Button
            variant="falcon-default"
            size="sm"
            onClick={() =>
              handlePrintPicklist(picklistId, picklistData?.picklist_code)
            }
          >
            <FontAwesomeIcon icon="file-alt" />
          </Button>
        </div>
      )}

      <Accordion activeKey={activeKeys} onSelect={handleSelect}>
        <Card>
          {/* General Information */}
          <Accordion.Item eventKey="0">
            <Accordion.Header className="d-flex justify-content-between">
              <span className="fw-semibold">General Information</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Picklist Code
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {picklistData?.picklist_code}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Status
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    <SubtleBadge
                      bg={getPicklistBadgeColor(picklistData?.picklist_status)}
                    >
                      {picklistData?.picklist_status_label}
                    </SubtleBadge>
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    No. Of Items
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {picklistData?.shipment_line_summary.total_quantity}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Summary */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <span className="fw-semibold">Summary</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Total Shipments
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {picklistData?.shipment_summary?.total_quantity || 0}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Packed Shipments
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {picklistData?.shipment_summary?.packed_quantity}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Ready To Ship Shipments
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {picklistData?.shipment_summary?.ready_to_ship_quantity}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Items Not Found
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {picklistData?.shipment_line_summary
                      ?.reject_not_found_quantity || 0}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Damaged Items
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {picklistData?.shipment_line_summary
                      ?.reject_damaged_quantity || 0}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Card>
      </Accordion>
    </>
  );
};

export default PicklistDetailSummary;
